import {useState, useEffect} from 'react'
import {Box, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import TextAreaNew from 'src/components-new/inputs/TextAreaNew'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import ParticipantLookupDialogNew from './ParticipantLookupDialogNew'

export interface VirtualMeetingRequestValues {
  title: string
  email: string
  notes?: string
  patient?: Record<string, any>
  medicalSupportService: boolean
  qualifications: string[]
}

interface VirtualMeetingRequestDialogNewProps {
  open: boolean
  qualifications: Record<string, any>[]
  onClose: () => void
  onSave: (values: VirtualMeetingRequestValues) => void | Promise<void>
}

export default function VirtualMeetingRequestDialogNew({
  open,
  qualifications,
  onClose,
  onSave,
}: VirtualMeetingRequestDialogNewProps): JSX.Element {
  const [title, setTitle] = useState('')
  const [patientName, setPatientName] = useState('')
  const [currentPatient, setCurrentPatient] = useState<Record<string, any> | undefined>()
  const [email, setEmail] = useState('')
  const [selectedQualifications, setSelectedQualifications] = useState<string[]>([])
  const [notes, setNotes] = useState('')
  const [lookupOpen, setLookupOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) {
      setTitle('')
      setPatientName('')
      setCurrentPatient(undefined)
      setEmail('')
      setSelectedQualifications([])
      setNotes('')
      setSubmitted(false)
      setSaving(false)
    }
  }, [open])

  const qualificationOptions = [
    {value: 'medicalSupportService', label: 'Medico del Servizio'},
    ...qualifications.map(q => ({value: q.name as string, label: q.name as string})),
  ]

  const handleSubmit = async () => {
    if (saving) return
    setSubmitted(true)
    if (!title.trim() || !patientName.trim() || !email.trim()) return

    const qualificationsArray = [...selectedQualifications]
    const index = qualificationsArray.indexOf('medicalSupportService')
    let medicalSupportService = false
    if (index !== -1) {
      qualificationsArray.splice(index, 1)
      medicalSupportService = true
    }

    setSaving(true)
    try {
      await onSave({
        title,
        email,
        notes,
        patient: currentPatient,
        medicalSupportService,
        qualifications: qualificationsArray,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <AppDialog
        actions={
          <>
            <Button disabled={saving} onClick={onClose} variant="secondary">Annulla</Button>
            <Button loading={saving} onClick={handleSubmit} variant="primary">Salva</Button>
          </>
        }
        maxWidth="sm"
        onClose={onClose}
        open={open}
        title="Richiedi incontro virtuale"
      >
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
          <TextFieldNew
            error={submitted && !title.trim()}
            helperText={submitted && !title.trim() ? 'Campo obbligatorio' : undefined}
            label="Titolo"
            onChange={setTitle}
            required
            value={title}
          />
          <Box>
            <Typography
              sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}
              variant="caption"
            >
              Paziente *
            </Typography>
            {currentPatient ? (
              <Box sx={{mt: 0.5}}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '8px',
                    backgroundColor: NEUTRAL_SCALE[50],
                    border: `1px solid ${NEUTRAL_SCALE[200]}`,
                  }}
                >
                  <Typography variant="body2">{patientName}</Typography>
                  <ActionIconButton
                    onClick={() => {
                      setCurrentPatient(undefined)
                      setPatientName('')
                    }}
                    tooltip="Rimuovi"
                    variant="close"
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{display: 'flex', gap: 1, mt: 0.5}}>
                <Button onClick={() => setLookupOpen(true)} startIcon={<SearchIcon fontSize="small" />} variant="ghost">
                  Aggiungi Paziente
                </Button>
              </Box>
            )}
            {submitted && !patientName.trim() && (
              <Typography sx={{color: 'error.main', display: 'block', mt: 0.5}} variant="caption">
                Campo obbligatorio
              </Typography>
            )}
          </Box>
          <TextFieldNew
            error={submitted && !email.trim()}
            helperText={submitted && !email.trim() ? 'Campo obbligatorio' : undefined}
            label="Email paziente"
            onChange={setEmail}
            required
            value={email}
          />
          <CheckboxGroupNew
            label="Richiesta la partecipazione di"
            onChange={setSelectedQualifications}
            options={qualificationOptions}
            value={selectedQualifications}
          />
          <TextAreaNew
            label="Date e fasce orarie di disponibilità ed eventuali Note"
            onChange={setNotes}
            rows={4}
            value={notes}
          />
        </Box>
      </AppDialog>

      {lookupOpen && (
        <ParticipantLookupDialogNew
          mode="patient"
          onClose={() => setLookupOpen(false)}
          onRowClick={(row) => {
            setCurrentPatient(row)
            setPatientName(`${row.firstName} ${row.lastName}`)
            setEmail(row.email ?? '')
            setLookupOpen(false)
          }}
        />
      )}
    </>
  )
}
